import Big from 'big.js'
import moment from 'moment'
import isDateBeforeRentRollLockDate from 'lib/utils'
import { rentRollLedgerLockDate } from 'lib/config'
import { isAllowedLiabilityType } from 'lib/liability-types'

import { canonicalAmount, canonicalDate } from './canonical'
import { LiabilityType, RentRollRow } from './types'

export const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const ACCEPTED_DATE_FORMATS = ['M/D/YY', 'MM/DD/YY', 'M/D/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']
const DATE_FORMAT_ERROR = 'must be MM/DD/YY, MM/DD/YYYY, or YYYY-MM-DD.'

export const parseAmount = (value?: string): { amount?: number; error?: string } => {
  if (!value || value.trim().length === 0) {
    return { error: 'Amount is required.' }
  }

  try {
    const amount = new Big(value.trim())
    if (amount.lte(0)) {
      return { error: 'Enter an amount greater than $0.00.' }
    }

    return { amount: Number(amount.toString()) }
  } catch (_error) {
    return { error: 'Amount must be a number.' }
  }
}

export const parseDate = (
  value?: string,
  label: string = 'Date'
): { date?: string; error?: string } => {
  if (!value || value.trim().length === 0) {
    return { error: `${label} is required.` }
  }

  const date = moment(value.trim(), ACCEPTED_DATE_FORMATS, true)
  if (!date.isValid()) {
    return { error: `${label} ${DATE_FORMAT_ERROR}` }
  }

  return { date: date.format('YYYY-MM-DD') }
}

export const validateModalParity = ({
  amount,
  date,
  liabilityType,
  note,
  rental
}: {
  amount?: number
  date?: string
  liabilityType?: LiabilityType
  note: string | null
  rental?: RentRollRow
}) => {
  const errors: string[] = []

  if (liabilityType && !isAllowedLiabilityType(liabilityType)) {
    errors.push('Liability type is not allowed for manual rental liability creation.')
  }

  if (
    rental?.occupancy_date &&
    date &&
    canonicalDate(date) < canonicalDate(rental.occupancy_date)
  ) {
    errors.push(`Liability date must be on or after the occupancy date (${rental.occupancy_date}).`)
  }

  if (date && isDateBeforeRentRollLockDate(date)) {
    errors.push(
      `Updates to rent-roll before ${rentRollLedgerLockDate} are not allowed while FinOps is doing a Rent Roll Audit.`
    )
  }

  if (
    rental?.status &&
    !['complete', 'canceled'].includes(rental.status) &&
    (!note || note.trim().length === 0)
  ) {
    errors.push('Note is required for active rentals.')
  }

  if (
    liabilityType &&
    liabilityType.is_variable === false &&
    liabilityType.price !== null &&
    liabilityType.price !== undefined &&
    amount !== undefined &&
    canonicalAmount(amount) !== canonicalAmount(liabilityType.price)
  ) {
    errors.push(`Amount must equal $${canonicalAmount(liabilityType.price)}.`)
  }

  return errors
}
